import React, { useState, useEffect } from 'react';
import { SportType } from '../types';
import {
  InjuryLog,
  RecoveryStretch,
  INITIAL_INJURY_LOGS,
  RECOVERY_STRETCHES,
  StretchStep,
} from '../data/recoveryData';
import {
  Activity,
  HeartPulse,
  Plus,
  Play,
  CheckCircle2,
  Clock,
  Sparkles,
  Flame,
  AlertTriangle,
  X,
  ChevronRight,
  RotateCcw,
  Trophy,
  ShieldAlert,
  Dumbbell,
  Info,
  Check,
  Pause,
  ArrowRight,
  Award
} from 'lucide-react';

interface InjuryRecoveryTrackerProps {
  primarySport: SportType;
  userXp: number;
  onClaimXp: (amount: number, reason: string) => void;
}

export const InjuryRecoveryTracker: React.FC<InjuryRecoveryTrackerProps> = ({
  primarySport,
  userXp,
  onClaimXp,
}) => {
  const [injuries, setInjuries] = useState<InjuryLog[]>(INITIAL_INJURY_LOGS);
  const [selectedSportFilter, setSelectedSportFilter] = useState<string>('all');
  const [selectedBodyPartFilter, setSelectedBodyPartFilter] = useState<string>('all');
  
  // Modals & Active Session State
  const [isLogModalOpen, setIsLogModalOpen] = useState(false);
  const [activeStretchModal, setActiveStretchModal] = useState<RecoveryStretch | null>(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [timeRemainingSec, setTimeRemainingSec] = useState(0);
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [completedSessionModal, setCompletedSessionModal] = useState<{ stretch: RecoveryStretch; xpEarned: number } | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  // New Injury Form State
  const [newBodyPart, setNewBodyPart] = useState('Ankle');
  const [newCustomBodyPart, setNewCustomBodyPart] = useState('');
  const [newSeverity, setNewSeverity] = useState<'mild' | 'moderate' | 'severe'>('mild');
  const [newPainLevel, setNewPainLevel] = useState(3);
  const [newSport, setNewSport] = useState<SportType>(primarySport || 'basketball');
  const [newNotes, setNewNotes] = useState('');

  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 3500);
  };

  // Timer Effect for active stretch session
  useEffect(() => {
    let timer: any = null;
    if (isTimerRunning && timeRemainingSec > 0) {
      timer = setInterval(() => {
        setTimeRemainingSec((prev) => prev - 1);
      }, 1000);
    } else if (isTimerRunning && timeRemainingSec === 0 && activeStretchModal) {
      // Step finished automatically -> move to next step or complete
      if (currentStepIndex < activeStretchModal.steps.length - 1) {
        const nextIdx = currentStepIndex + 1;
        setCurrentStepIndex(nextIdx);
        setTimeRemainingSec(activeStretchModal.steps[nextIdx].durationSec);
      } else {
        // Session Complete!
        finishStretchSession(activeStretchModal);
      }
    }
    return () => clearInterval(timer);
  }, [isTimerRunning, timeRemainingSec, currentStepIndex, activeStretchModal]);

  const startStretchSession = (stretch: RecoveryStretch) => {
    setActiveStretchModal(stretch);
    setCurrentStepIndex(0);
    setTimeRemainingSec(stretch.steps[0].durationSec);
    setIsTimerRunning(true);
  };

  const finishStretchSession = (stretch: RecoveryStretch) => {
    setIsTimerRunning(false);

    // Award XP
    onClaimXp(stretch.xpReward, `Completed Guided Stretch: ${stretch.title}`);

    // Update active injuries progress if matching
    setInjuries((prev) =>
      prev.map((inj) => {
        if (
          inj.status !== 'recovered' &&
          (inj.sport === stretch.sport || stretch.bodyPart.toLowerCase().includes(inj.bodyPart.toLowerCase()))
        ) {
          const newProg = Math.min(100, inj.recoveryProgress + 25);
          const isNowRecovered = newProg === 100;
          return {
            ...inj,
            recoveryProgress: newProg,
            completedStretchesCount: inj.completedStretchesCount + 1,
            status: isNowRecovered ? 'recovered' : 'recovering',
          };
        }
        return inj;
      })
    );

    setCompletedSessionModal({ stretch, xpEarned: stretch.xpReward });
    setActiveStretchModal(null);
  };

  const handleNextStep = () => {
    if (!activeStretchModal) return;
    if (currentStepIndex < activeStretchModal.steps.length - 1) {
      const nextIdx = currentStepIndex + 1;
      setCurrentStepIndex(nextIdx);
      setTimeRemainingSec(activeStretchModal.steps[nextIdx].durationSec);
    } else {
      finishStretchSession(activeStretchModal);
    }
  };

  const handlePrevStep = () => {
    if (!activeStretchModal) return;
    if (currentStepIndex > 0) {
      const prevIdx = currentStepIndex - 1;
      setCurrentStepIndex(prevIdx);
      setTimeRemainingSec(activeStretchModal.steps[prevIdx].durationSec);
    }
  };

  const handleAddInjury = (e: React.FormEvent) => {
    e.preventDefault();
    const bodyPartName = newBodyPart === 'Other' ? (newCustomBodyPart || 'General Muscle') : newBodyPart;
    const newLog: InjuryLog = {
      id: `inj_${Date.now()}`,
      bodyPart: bodyPartName,
      severity: newSeverity,
      painLevel: newPainLevel,
      sport: newSport,
      loggedDate: new Date().toISOString().split('T')[0],
      notes: newNotes || 'Logged from Profile recovery portal.',
      status: 'active',
      recoveryProgress: 10,
      completedStretchesCount: 0,
    };

    setInjuries([newLog, ...injuries]);
    setIsLogModalOpen(false);
    showToast(`📍 Logged minor injury: ${bodyPartName}. Follow stretches to earn recovery XP!`);
    
    // Reset form
    setNewNotes('');
    setNewCustomBodyPart('');
  };

  const handleMarkRecovered = (id: string) => {
    setInjuries((prev) =>
      prev.map((inj) => {
        if (inj.id === id) {
          return {
            ...inj,
            status: 'recovered',
            recoveryProgress: 100,
          };
        }
        return inj;
      })
    );
    onClaimXp(150, 'Full Injury Recovery Completed! 🎉');
    showToast('🎉 Injury marked as Recovered! +150 Bonus XP awarded!');
  };

  const handleDeleteInjury = (id: string) => {
    setInjuries((prev) => prev.filter((i) => i.id !== id));
    showToast('Injury log removed.');
  };

  // Filter Stretches
  const filteredStretches = RECOVERY_STRETCHES.filter((st) => {
    const matchesSport = selectedSportFilter === 'all' || st.sport === selectedSportFilter;
    const matchesBody =
      selectedBodyPartFilter === 'all' ||
      st.bodyPart.toLowerCase().includes(selectedBodyPartFilter.toLowerCase());
    return matchesSport && matchesBody;
  });

  const activeInjuriesCount = injuries.filter((i) => i.status !== 'recovered').length;
  const recoveredInjuriesCount = injuries.filter((i) => i.status === 'recovered').length;

  return (
    <div className="space-y-6 text-white animate-fadeIn">
      {/* Toast Notification */}
      {toastMessage && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 bg-lime-400 text-black px-4 py-2.5 rounded-2xl font-black italic uppercase text-xs shadow-2xl border border-black flex items-center space-x-2 animate-bounce">
          <Sparkles className="w-4 h-4 text-black" />
          <span>{toastMessage}</span>
        </div>
      )}

      {/* Top Banner Card */}
      <div className="bg-gradient-to-r from-indigo-950 via-slate-900 to-indigo-900 p-6 rounded-[2.5rem] border border-white/10 shadow-xl space-y-4 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-lime-400/5 rounded-full blur-3xl pointer-events-none" />

        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 relative z-10">
          <div className="space-y-1">
            <div className="flex items-center space-x-2">
              <HeartPulse className="w-6 h-6 text-lime-400 animate-pulse" />
              <h2 className="text-xl font-black italic uppercase tracking-tight text-white">
                Injury & Recovery Portal
              </h2>
            </div>
            <p className="text-xs text-indigo-200/80 max-w-xl font-medium">
              Log minor athletic strains, follow sport-specific guided stretching protocols, restore peak joint mobility, and earn extra XP as you recover.
            </p>
          </div>

          <button
            type="button"
            onClick={() => setIsLogModalOpen(true)}
            className="px-4 py-2.5 bg-lime-400 hover:bg-lime-300 text-black font-black italic uppercase text-xs rounded-xl shadow-lg shadow-lime-400/20 flex items-center justify-center space-x-2 transition shrink-0"
          >
            <Plus className="w-4 h-4 stroke-[3]" />
            <span>Log Minor Injury</span>
          </button>
        </div>

        {/* Quick Recovery Stat Pills */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2 border-t border-white/10 relative z-10">
          <div className="bg-indigo-900/60 p-3 rounded-2xl border border-white/10 flex items-center space-x-3">
            <div className="p-2 rounded-xl bg-amber-400/20 text-amber-300">
              <ShieldAlert className="w-5 h-5" />
            </div>
            <div>
              <span className="text-[10px] text-indigo-200/70 font-extrabold block uppercase">Active Strains</span>
              <span className="text-base font-black text-amber-300">{activeInjuriesCount} Logged</span>
            </div>
          </div>

          <div className="bg-indigo-900/60 p-3 rounded-2xl border border-white/10 flex items-center space-x-3">
            <div className="p-2 rounded-xl bg-emerald-400/20 text-emerald-300">
              <CheckCircle2 className="w-5 h-5" />
            </div>
            <div>
              <span className="text-[10px] text-indigo-200/70 font-extrabold block uppercase">Recovered</span>
              <span className="text-base font-black text-emerald-300">{recoveredInjuriesCount} Healed</span>
            </div>
          </div>

          <div className="bg-indigo-900/60 p-3 rounded-2xl border border-white/10 flex items-center space-x-3">
            <div className="p-2 rounded-xl bg-lime-400/20 text-lime-400">
              <Dumbbell className="w-5 h-5" />
            </div>
            <div>
              <span className="text-[10px] text-indigo-200/70 font-extrabold block uppercase">Routines Ready</span>
              <span className="text-base font-black text-white">{RECOVERY_STRETCHES.length} Protocols</span>
            </div>
          </div>

          <div className="bg-indigo-900/60 p-3 rounded-2xl border border-white/10 flex items-center space-x-3">
            <div className="p-2 rounded-xl bg-purple-400/20 text-purple-300">
              <Trophy className="w-5 h-5" />
            </div>
            <div>
              <span className="text-[10px] text-indigo-200/70 font-extrabold block uppercase">Recovery Reward</span>
              <span className="text-base font-black text-lime-400">+75-150 XP</span>
            </div>
          </div>
        </div>
      </div>

      {/* SECTION 1: ACTIVE INJURY LOGS */}
      <div className="bg-indigo-900/60 p-6 rounded-[2.5rem] border border-white/10 shadow-xl space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-black italic uppercase text-white text-base flex items-center space-x-2">
              <Activity className="w-5 h-5 text-lime-400" />
              <span>Your Logged Strains & Injury Tracker</span>
            </h3>
            <p className="text-xs text-indigo-200/70 font-semibold">
              Track pain levels and complete targeted stretches to reach 100% recovery.
            </p>
          </div>

          <span className="text-xs font-mono font-bold bg-indigo-950 px-3 py-1 rounded-xl border border-white/10 text-indigo-300">
            {injuries.length} Total Logs
          </span>
        </div>

        {injuries.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {injuries.map((inj) => {
              const isRecovered = inj.status === 'recovered';
              let painColor = 'bg-amber-400/20 text-amber-300 border-amber-400/40';
              if (inj.painLevel >= 7) painColor = 'bg-rose-500/20 text-rose-300 border-rose-500/40';
              if (inj.painLevel <= 3) painColor = 'bg-emerald-400/20 text-emerald-300 border-emerald-400/40';

              return (
                <div
                  key={inj.id}
                  className={`p-4 rounded-2xl border transition space-y-3 relative overflow-hidden ${
                    isRecovered
                      ? 'bg-indigo-950/70 border-emerald-400/30'
                      : 'bg-indigo-950 border-white/10 hover:border-lime-400/40'
                  }`}
                >
                  {/* Top line info */}
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="flex items-center space-x-2">
                        <span className="font-black text-sm text-white">{inj.bodyPart}</span>
                        <span className="text-[10px] font-mono uppercase bg-indigo-900 px-2 py-0.5 rounded-md border border-white/10 text-indigo-200">
                          {inj.sport}
                        </span>
                      </div>
                      <span className="text-[10px] text-indigo-300/70 font-mono block mt-0.5">
                        Logged on {inj.loggedDate}
                      </span>
                    </div>

                    <div className="flex items-center space-x-1.5">
                      <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded-lg border ${painColor}`}>
                        Pain Level: {inj.painLevel}/10
                      </span>

                      <button
                        type="button"
                        onClick={() => handleDeleteInjury(inj.id)}
                        className="text-indigo-400 hover:text-rose-400 p-1 rounded-md transition"
                        title="Delete log"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>

                  {/* Notes */}
                  <p className="text-xs text-indigo-200/80 font-medium italic bg-indigo-900/40 p-2.5 rounded-xl border border-white/5">
                    "{inj.notes}"
                  </p>

                  {/* Recovery Progress Bar */}
                  <div className="space-y-1">
                    <div className="flex justify-between text-[11px] font-bold">
                      <span className="text-indigo-200/70">Recovery Progress</span>
                      <span className={isRecovered ? 'text-emerald-400 font-extrabold' : 'text-lime-400'}>
                        {inj.recoveryProgress}% {isRecovered ? '✓ Recovered' : ''}
                      </span>
                    </div>
                    <div className="w-full bg-indigo-900 h-2.5 rounded-full overflow-hidden border border-white/10">
                      <div
                        className={`h-full transition-all duration-500 rounded-full ${
                          isRecovered
                            ? 'bg-gradient-to-r from-emerald-400 to-teal-300'
                            : 'bg-gradient-to-r from-lime-400 to-emerald-400'
                        }`}
                        style={{ width: `${inj.recoveryProgress}%` }}
                      />
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div className="flex items-center justify-between pt-1 text-xs">
                    <span className="text-[10px] text-indigo-300 font-mono">
                      Stretches Done: {inj.completedStretchesCount}
                    </span>

                    {!isRecovered ? (
                      <div className="flex items-center space-x-2">
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedBodyPartFilter(inj.bodyPart.split(' ')[0]);
                            showToast(`Filtered stretches for ${inj.bodyPart}!`);
                          }}
                          className="px-2.5 py-1 bg-indigo-900 hover:bg-indigo-800 text-indigo-100 rounded-lg font-bold text-[11px] transition border border-white/10 flex items-center space-x-1"
                        >
                          <Dumbbell className="w-3 h-3 text-lime-400" />
                          <span>Find Stretches</span>
                        </button>

                        <button
                          type="button"
                          onClick={() => handleMarkRecovered(inj.id)}
                          className="px-2.5 py-1 bg-emerald-400 hover:bg-emerald-300 text-black rounded-lg font-black italic uppercase text-[11px] transition shadow flex items-center space-x-1"
                        >
                          <CheckCircle2 className="w-3 h-3" />
                          <span>Mark Recovered (+150 XP)</span>
                        </button>
                      </div>
                    ) : (
                      <span className="text-xs font-black text-emerald-400 italic flex items-center space-x-1">
                        <Check className="w-4 h-4 stroke-[3]" />
                        <span>Ready for Full Action</span>
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="p-6 bg-indigo-950/60 rounded-2xl border border-dashed border-white/10 text-center space-y-2">
            <HeartPulse className="w-8 h-8 text-lime-400/60 mx-auto" />
            <p className="text-xs font-bold text-indigo-200">No active injuries logged.</p>
            <p className="text-[10px] text-indigo-200/60 max-w-xs mx-auto">
              Feeling 100% healthy? You can still follow guided recovery stretches below to earn extra daily XP!
            </p>
          </div>
        )}
      </div>

      {/* SECTION 2: GUIDED RECOVERY STRETCHES & ROUTINES */}
      <div className="bg-indigo-900/60 p-6 rounded-[2.5rem] border border-white/10 shadow-xl space-y-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h3 className="font-black italic uppercase text-white text-base flex items-center space-x-2">
              <Dumbbell className="w-5 h-5 text-lime-400" />
              <span>Sport-Specific Guided Recovery Stretches</span>
            </h3>
            <p className="text-xs text-indigo-200/70 font-semibold">
              Select a routine to launch step-by-step interactive stretch timer and claim +75-100 XP.
            </p>
          </div>

          <div className="flex items-center space-x-2 text-xs">
            <span className="text-indigo-300 font-bold hidden sm:inline">Filters:</span>
            
            {/* Sport Filter Dropdown */}
            <select
              value={selectedSportFilter}
              onChange={(e) => setSelectedSportFilter(e.target.value)}
              className="px-3 py-1.5 bg-indigo-950 text-white rounded-xl border border-white/10 font-bold outline-none focus:ring-1 focus:ring-lime-400"
            >
              <option value="all">All Sports</option>
              <option value="basketball">Basketball 🏀</option>
              <option value="soccer">Soccer ⚽</option>
              <option value="volleyball">Volleyball 🏐</option>
              <option value="football">Football 🏈</option>
              <option value="tennis">Tennis 🎾</option>
            </select>

            {/* Body Part Filter Dropdown */}
            <select
              value={selectedBodyPartFilter}
              onChange={(e) => setSelectedBodyPartFilter(e.target.value)}
              className="px-3 py-1.5 bg-indigo-950 text-white rounded-xl border border-white/10 font-bold outline-none focus:ring-1 focus:ring-lime-400"
            >
              <option value="all">All Muscle Groups</option>
              <option value="Ankle">Ankle & Achilles</option>
              <option value="Knee">Knee & Quads</option>
              <option value="Groin">Groin & Hips</option>
              <option value="Shoulder">Shoulder & Rotator Cuff</option>
              <option value="Hamstring">Hamstring</option>
              <option value="Lower Back">Lower Back</option>
            </select>
          </div>
        </div>

        {/* Stretches Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredStretches.map((stretch) => (
            <div
              key={stretch.id}
              className="bg-indigo-950 rounded-2xl overflow-hidden border border-white/10 hover:border-lime-400/50 transition duration-200 flex flex-col justify-between group shadow-lg"
            >
              {/* Image Banner & Header */}
              <div>
                <div className="relative h-32 w-full overflow-hidden bg-slate-900">
                  <img
                    src={stretch.thumbnailUrl}
                    alt={stretch.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition duration-300 opacity-80"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-indigo-950 via-indigo-950/30 to-transparent" />

                  {/* Badges */}
                  <div className="absolute top-2 left-2 flex items-center space-x-1.5">
                    <span className="bg-lime-400 text-black font-black italic uppercase text-[9px] px-2 py-0.5 rounded-lg shadow">
                      {stretch.sport}
                    </span>
                    <span className="bg-indigo-950/80 border border-white/20 text-indigo-200 font-bold text-[9px] px-2 py-0.5 rounded-lg backdrop-blur-md">
                      {stretch.bodyPart}
                    </span>
                  </div>

                  <div className="absolute top-2 right-2 bg-purple-500/90 text-white font-black text-[10px] px-2 py-0.5 rounded-lg shadow flex items-center space-x-1">
                    <Trophy className="w-3 h-3 text-lime-300" />
                    <span>+{stretch.xpReward} XP</span>
                  </div>

                  <div className="absolute bottom-2 left-3 right-3 flex items-center justify-between text-[11px] text-indigo-200 font-mono">
                    <span className="flex items-center space-x-1">
                      <Clock className="w-3.5 h-3.5 text-lime-400" />
                      <span>{stretch.durationMinutes} mins</span>
                    </span>
                    <span className="bg-indigo-900/80 px-2 py-0.5 rounded-md text-[10px] font-extrabold text-white">
                      {stretch.steps.length} Steps
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-4 space-y-2">
                  <h4 className="font-extrabold text-sm text-white group-hover:text-lime-300 transition line-clamp-1">
                    {stretch.title}
                  </h4>
                  <p className="text-xs text-indigo-200/70 line-clamp-2 leading-relaxed">
                    {stretch.description}
                  </p>

                  <div className="pt-1 text-[11px] text-emerald-300/90 font-medium flex items-center space-x-1">
                    <Sparkles className="w-3 h-3 text-lime-400 shrink-0" />
                    <span className="line-clamp-1">{stretch.benefits}</span>
                  </div>
                </div>
              </div>

              {/* Start Session Button */}
              <div className="p-4 pt-0">
                <button
                  type="button"
                  onClick={() => startStretchSession(stretch)}
                  className="w-full py-2.5 bg-gradient-to-r from-lime-400 to-emerald-400 text-black font-black italic uppercase text-xs rounded-xl shadow-lg hover:brightness-110 transition flex items-center justify-center space-x-1.5"
                >
                  <Play className="w-4 h-4 fill-black" />
                  <span>Start Guided Routine (+{stretch.xpReward} XP)</span>
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* MODAL 1: LOG NEW INJURY MODAL */}
      {isLogModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4">
          <div className="bg-indigo-950 border-2 border-lime-400/50 rounded-[2.5rem] max-w-lg w-full p-6 text-white space-y-4 shadow-2xl relative animate-scaleUp">
            <div className="flex items-center justify-between border-b border-white/10 pb-3">
              <div className="flex items-center space-x-2">
                <HeartPulse className="w-5 h-5 text-lime-400" />
                <h3 className="font-black text-base italic uppercase">Log Minor Injury or Muscle Strain</h3>
              </div>
              <button
                type="button"
                onClick={() => setIsLogModalOpen(false)}
                className="p-1 rounded-full bg-indigo-900 hover:bg-lime-400 hover:text-black transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleAddInjury} className="space-y-4 text-xs font-semibold">
              <div>
                <label className="text-indigo-200/80 block mb-1 font-bold">Affected Body Part / Muscle</label>
                <select
                  value={newBodyPart}
                  onChange={(e) => setNewBodyPart(e.target.value)}
                  className="w-full p-3 bg-indigo-900 text-white rounded-xl border border-white/10 outline-none focus:ring-2 focus:ring-lime-400 font-bold"
                >
                  <option value="Right Ankle">Right Ankle</option>
                  <option value="Left Ankle">Left Ankle</option>
                  <option value="Knee / Patellar">Knee / Patellar Tendon</option>
                  <option value="Hamstring">Hamstring Strain</option>
                  <option value="Groin / Adductor">Groin / Adductor</option>
                  <option value="Shoulder / Rotator Cuff">Shoulder / Rotator Cuff</option>
                  <option value="Lower Back">Lower Back / Lumbar</option>
                  <option value="Wrist / Forearm">Wrist / Forearm</option>
                  <option value="Other">Other Custom Area</option>
                </select>
              </div>

              {newBodyPart === 'Other' && (
                <div>
                  <label className="text-indigo-200/80 block mb-1 font-bold">Custom Body Area Name</label>
                  <input
                    type="text"
                    placeholder="e.g., Plantar Fascia, Calves"
                    value={newCustomBodyPart}
                    onChange={(e) => setNewCustomBodyPart(e.target.value)}
                    className="w-full p-3 bg-indigo-900 text-white rounded-xl border border-white/10 outline-none focus:ring-2 focus:ring-lime-400"
                  />
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-indigo-200/80 block mb-1 font-bold">Sport Context</label>
                  <select
                    value={newSport}
                    onChange={(e) => setNewSport(e.target.value as SportType)}
                    className="w-full p-3 bg-indigo-900 text-white rounded-xl border border-white/10 outline-none focus:ring-2 focus:ring-lime-400 font-bold uppercase"
                  >
                    <option value="basketball">Basketball</option>
                    <option value="soccer">Soccer</option>
                    <option value="volleyball">Volleyball</option>
                    <option value="football">Football</option>
                    <option value="baseball">Baseball</option>
                    <option value="tennis">Tennis</option>
                  </select>
                </div>

                <div>
                  <label className="text-indigo-200/80 block mb-1 font-bold">Initial Severity</label>
                  <select
                    value={newSeverity}
                    onChange={(e) => setNewSeverity(e.target.value as any)}
                    className="w-full p-3 bg-indigo-900 text-white rounded-xl border border-white/10 outline-none focus:ring-2 focus:ring-lime-400 font-bold"
                  >
                    <option value="mild">Mild (Tightness)</option>
                    <option value="moderate">Moderate (Pain on movement)</option>
                    <option value="severe">Severe (Rest required)</option>
                  </select>
                </div>
              </div>

              {/* Pain Scale Selector */}
              <div>
                <div className="flex justify-between items-center mb-1">
                  <label className="text-indigo-200/80 font-bold">Pain Scale (1 to 10)</label>
                  <span className="text-lime-400 font-black text-sm">{newPainLevel} / 10</span>
                </div>
                <input
                  type="range"
                  min="1"
                  max="10"
                  value={newPainLevel}
                  onChange={(e) => setNewPainLevel(Number(e.target.value))}
                  className="w-full accent-lime-400 cursor-pointer"
                />
              </div>

              <div>
                <label className="text-indigo-200/80 block mb-1 font-bold">Notes & Incident Details</label>
                <textarea
                  rows={3}
                  placeholder="e.g. Felt tight landing after game on Court 1..."
                  value={newNotes}
                  onChange={(e) => setNewNotes(e.target.value)}
                  className="w-full p-3 bg-indigo-900 text-white rounded-xl border border-white/10 outline-none focus:ring-2 focus:ring-lime-400"
                />
              </div>

              <div className="pt-2 flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => setIsLogModalOpen(false)}
                  className="px-4 py-2.5 bg-indigo-900 hover:bg-indigo-800 text-indigo-200 font-bold rounded-xl"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2.5 bg-lime-400 hover:bg-lime-300 text-black font-black italic uppercase rounded-xl shadow-lg"
                >
                  Save Injury Log
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 2: GUIDED INTERACTIVE STRETCH SESSION MODAL */}
      {activeStretchModal && (
        <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-lg flex items-center justify-center p-4">
          <div className="bg-indigo-950 border-2 border-lime-400 rounded-[2.5rem] max-w-xl w-full p-6 text-white space-y-6 shadow-2xl relative animate-scaleUp">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-white/10 pb-4">
              <div>
                <span className="text-[10px] font-black uppercase text-lime-400 italic">
                  Guided Routine • {activeStretchModal.sport}
                </span>
                <h3 className="text-lg font-black italic uppercase text-white">{activeStretchModal.title}</h3>
              </div>
              <button
                type="button"
                onClick={() => setActiveStretchModal(null)}
                className="p-1.5 rounded-full bg-indigo-900 hover:bg-rose-500 hover:text-white transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Step Progression Bar */}
            <div className="space-y-1">
              <div className="flex justify-between text-xs font-bold text-indigo-200">
                <span>
                  Step {currentStepIndex + 1} of {activeStretchModal.steps.length}
                </span>
                <span className="text-lime-400 font-mono">
                  {activeStretchModal.steps[currentStepIndex].title}
                </span>
              </div>
              <div className="flex space-x-1.5">
                {activeStretchModal.steps.map((_, idx) => (
                  <div
                    key={idx}
                    className={`h-2 flex-1 rounded-full transition-all duration-300 ${
                      idx === currentStepIndex
                        ? 'bg-lime-400 shadow-lg shadow-lime-400/50'
                        : idx < currentStepIndex
                        ? 'bg-emerald-400'
                        : 'bg-indigo-900'
                    }`}
                  />
                ))}
              </div>
            </div>

            {/* Interactive Timer Box */}
            <div className="p-6 bg-gradient-to-b from-indigo-900 to-indigo-950 rounded-3xl border border-white/10 text-center space-y-4 shadow-inner relative overflow-hidden">
              <div className="space-y-1">
                <span className="text-xs font-black uppercase text-indigo-300 tracking-wider">
                  {activeStretchModal.steps[currentStepIndex].title}
                </span>
                <p className="text-sm text-indigo-100 font-medium max-w-md mx-auto leading-relaxed">
                  {activeStretchModal.steps[currentStepIndex].instruction}
                </p>
              </div>

              {/* Countdown Clock Display */}
              <div className="py-2">
                <span className="text-5xl font-black font-mono text-lime-400 tracking-wider drop-shadow-md">
                  00:{timeRemainingSec < 10 ? `0${timeRemainingSec}` : timeRemainingSec}
                </span>
              </div>

              {/* Play / Pause / Skip Controls */}
              <div className="flex items-center justify-center space-x-4 pt-2">
                <button
                  type="button"
                  onClick={handlePrevStep}
                  disabled={currentStepIndex === 0}
                  className="p-3 bg-indigo-900 hover:bg-indigo-800 text-white rounded-2xl disabled:opacity-30 transition"
                  title="Previous step"
                >
                  <RotateCcw className="w-5 h-5" />
                </button>

                <button
                  type="button"
                  onClick={() => setIsTimerRunning(!isTimerRunning)}
                  className="px-6 py-3 bg-lime-400 hover:bg-lime-300 text-black font-black italic uppercase rounded-2xl shadow-xl flex items-center space-x-2 text-sm transition"
                >
                  {isTimerRunning ? (
                    <>
                      <Pause className="w-5 h-5 fill-black" />
                      <span>Pause</span>
                    </>
                  ) : (
                    <>
                      <Play className="w-5 h-5 fill-black" />
                      <span>Resume Timer</span>
                    </>
                  )}
                </button>

                <button
                  type="button"
                  onClick={handleNextStep}
                  className="p-3 bg-indigo-900 hover:bg-indigo-800 text-lime-400 rounded-2xl transition"
                  title="Skip to next step"
                >
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Quick Finish Button */}
            <div className="flex justify-between items-center text-xs">
              <span className="text-indigo-300 font-mono">
                Completion Bonus: +{activeStretchModal.xpReward} XP
              </span>
              <button
                type="button"
                onClick={() => finishStretchSession(activeStretchModal)}
                className="px-4 py-2 bg-indigo-900 hover:bg-lime-400 hover:text-black font-black italic uppercase text-xs rounded-xl border border-white/10 transition"
              >
                Complete Routine Now ✓
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL 3: SESSION COMPLETED CELEBRATION MODAL */}
      {completedSessionModal && (
        <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-lg flex items-center justify-center p-4">
          <div className="bg-indigo-950 border-2 border-lime-400 rounded-[2.5rem] max-w-md w-full p-6 text-white text-center space-y-4 shadow-2xl animate-bounceIn">
            <div className="w-16 h-16 bg-lime-400 text-black rounded-full flex items-center justify-center mx-auto shadow-2xl">
              <Trophy className="w-8 h-8" />
            </div>

            <div className="space-y-1">
              <h3 className="text-xl font-black italic uppercase text-lime-400">Recovery Routine Complete!</h3>
              <p className="text-xs text-indigo-200/80 font-medium">
                You successfully completed <span className="text-white font-bold">{completedSessionModal.stretch.title}</span>.
              </p>
            </div>

            <div className="p-4 bg-lime-400/10 border border-lime-400/30 rounded-2xl">
              <span className="text-2xl font-black text-lime-300">+{completedSessionModal.xpEarned} XP CLAIMED! 🎉</span>
              <p className="text-[11px] text-indigo-200/70 mt-1">
                Your joint mobility & recovery progress has been recorded.
              </p>
            </div>

            <button
              type="button"
              onClick={() => setCompletedSessionModal(null)}
              className="w-full py-3 bg-lime-400 hover:bg-lime-300 text-black font-black italic uppercase text-xs rounded-xl shadow-lg"
            >
              Back to Profile
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
